/**
 * /instances - Customer's orboto-workspace overview (OCP-204).
 *
 * Lists every tenant the authenticated customer owns. Each card links
 * to /instances/:id for status + actions. Live provisioning progress
 * (status=provisioning) lands in OCP-205 on the detail page.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../lib/api';
import { fmtDate } from '../lib/format-time';

interface Instance {
  id: string;
  subdomain: string;
  custom_domain: string | null;
  status: string;
  subscription_state: string;
  region: string;
  license_expires_at: string | null;
  pool_tag: string;
  storage_quota_gb: number;
  created_at: string;
  paused_at: string | null;
  tenant_url: string;
  is_provisioning: boolean;
  /** OCP-643 - the instance login is a separate account; true = instance password not set yet. */
  admin_password_pending?: boolean | null;
  setup_link_resend_available?: boolean;
  /** OCP-850 - deleted after the export window (null = not deleted). */
  deleted_at?: string | null;
  deletion_record_available?: boolean;
}

/** Badge colours per status; the label is `instances:status.<key>` (OCP-656). */
const STATUS_BADGE: Record<string, string> = {
  provisioning: 'bg-sky-100 text-sky-800',
  active: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  past_due: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200',
  paused: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  archived: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  // OCP-850 - deleted after the export window.
  deleted: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
};

export function InstancesPage() {
  const { t } = useTranslation('instances');
  const [items, setItems] = useState<Instance[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const r = await api<{ items: Instance[] }>('/v1/portal/instances', {
          signal: ac.signal,
        });
        setItems(r.items);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError) setError(t('loadFailedHttp', { status: err.status }));
        else setError(t('loadFailed'));
      }
    })();
    return () => ac.abort();
  }, [t]);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('intro')}</p>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {!items && !error && (
        <p className="text-sm text-gray-500">{t('loading')}</p>
      )}

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          {t('empty.before')}{' '}
          <a
            href="https://orboto.io/redeem"
            className="text-brand-700 underline"
          >
            {t('empty.link')}
          </a>{' '}
          {t('empty.after')}
        </div>
      )}

      <div className="space-y-3">
        {items?.map((i) => (
          <InstanceCard key={i.id} instance={i} />
        ))}
      </div>
    </div>
  );
}

function InstanceCard({ instance }: { instance: Instance }) {
  const { t } = useTranslation('instances');
  const statusKey = instance.deleted_at ? 'deleted' : instance.status;
  const badge = {
    label: statusKey in STATUS_BADGE ? t(`status.${statusKey}`) : instance.status,
    cls: STATUS_BADGE[statusKey] ?? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  };
  const displayHost = instance.custom_domain ?? `${instance.subdomain}.orbo.to`;
  return (
    <Link
      to={`/instances/${instance.id}`}
      data-testid={`instance-card-${instance.id}`}
      className="block rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50/30"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {displayHost}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {instance.region} {dotSep()} {instance.pool_tag}
            {instance.storage_quota_gb
              ? ` ${dotSep()} ${t('card.storage', { gb: instance.storage_quota_gb })}`
              : ''}
          </p>
        </div>
        <span
          data-testid={`instance-status-${instance.id}`}
          className={`rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}
        >
          {badge.label}
        </span>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        {t('card.created', { date: formatDate(instance.created_at) })}
        {instance.license_expires_at &&
          ` ${dotSep()} ${t('card.licenseEnds', { date: formatDate(instance.license_expires_at) })}`}
      </p>
      {/* OCP-850 - deleted after the export window; the record is on the detail page. */}
      {instance.deleted_at && (
        <p data-testid={`instance-deleted-${instance.id}`} className="mt-2 text-xs text-gray-600 dark:text-gray-400">
          {instance.deletion_record_available
            ? t('card.deletedWithRecord', { date: formatDate(instance.deleted_at) })
            : t('card.deleted', { date: formatDate(instance.deleted_at) })}
        </p>
      )}
      {/* OCP-643 - the workspace login is separate from this account. */}
      {instance.admin_password_pending === true && (
        <p
          data-testid={`instance-password-pending-${instance.id}`}
          className="mt-2 rounded bg-amber-50 dark:bg-amber-900/30 px-2 py-1 text-xs text-amber-900 dark:text-amber-200"
        >
          {t('card.passwordPending')}
        </p>
      )}
    </Link>
  );
}

function dotSep(): string {
  return '·';
}

function formatDate(iso: string | null): string {
  return fmtDate(iso, '-');
}
